import React, { PropsWithChildren, useEffect, useMemo } from "react"
import { Form, Formik, FormikHelpers, FormikValues, useFormikContext } from "formik"
import { Logger } from "shared/Helpers/logging"
import {
    FormikFloatingSubmitButton,
    FormikSubmitButton
} from "shared/Components/Form/Formik"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { FormInputField } from "shared/Modules/Forms/formTypes"
import { generateFormSchema, getInitialValues } from "shared/Modules/Forms/formLib"
import { IPaymentMethod, WebshopPaymentDetails } from "shared/Modules/Transaction/transactionTypes"
import { useAccountingFields } from "./AccountingSection"
import { useConditionFields } from "./ConditionsSection"
import { IEditMeetingOrderData } from "../../../Types/MeetingPlanning/deliveryForm"
import { useDeliveryFormFields } from "./MeetingPlanningDeliveryForm"
import { useExtraServicesFormFields } from "./MeetingPlanningExtraServicesForm"
import { useDeliveryPaymentFormFields } from "./MeetingPlanningPaymentDeliveryForm"
import { IMeetingServiceConfig } from "../../../Types/MeetingPlanning/meetingServiceConfiguration"
import { useLocalization } from "@fluent/react"

type FormUpdaterProps = Readonly<{
    fields: FormInputField[]
}>

function FormUpdater({ fields }: FormUpdaterProps) {
    const formik = useFormikContext()
    const logger = new Logger("basket")

    useEffect(() => {
        logger.info("Resetting form")
        formik.resetForm()
    }, [fields])

    return null
}

type BasketFormProps = Readonly<PropsWithChildren<{
    initialMeetingPlanningData: IEditMeetingOrderData | null
    meetingServiceConfigData: IMeetingServiceConfig | null
    paymentDetails: WebshopPaymentDetails
    selectedPaymentMethod: IPaymentMethod | undefined
    onSubmit: (values: FormikValues, helpers: FormikHelpers<FormikValues>) => Promise<void>
    isMeetingPlanning: boolean
}>>

export function BasketForm({ initialMeetingPlanningData, meetingServiceConfigData, isMeetingPlanning, paymentDetails, selectedPaymentMethod, children, onSubmit }: BasketFormProps) {
    const logger = new Logger("basket")

    const { l10n } = useLocalization()
    const requiredMessage = l10n.getString('error-required', undefined, 'Dette felt skal udfyldes')
    const costCenterFields = useAccountingFields({ dimensions: selectedPaymentMethod?.accounting?.dimensions })
    const conditionFields = useConditionFields({ paymentDetails, selectedPaymentMethod })

    const deliveryFormFields = useDeliveryFormFields(initialMeetingPlanningData, meetingServiceConfigData, requiredMessage, l10n)
    const extraServicesFormFields = useExtraServicesFormFields(initialMeetingPlanningData)
    const deliveryPaymentFormFields = useDeliveryPaymentFormFields(initialMeetingPlanningData, meetingServiceConfigData, requiredMessage, l10n)

    const allFields = useMemo(() => {
        const deliveryFields = isMeetingPlanning ? [...deliveryFormFields, ...extraServicesFormFields, ...deliveryPaymentFormFields] : []
        return [ ...costCenterFields, ...conditionFields, ...deliveryFields ]
    }, [costCenterFields, conditionFields, isMeetingPlanning])

    logger.info("Form fields:", allFields)

    const initialValues = useMemo(() => getInitialValues(allFields), [allFields])
    const validationSchema = useMemo(() => generateFormSchema(allFields), [allFields])

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            <Form>
                <FormUpdater fields={allFields} />
                {children}
                <LocalizedStrict id="order-food-basket-purchase-button" attrs={{ loadingLabel: true }}>
                    {!isMeetingPlanning
                        ? <FormikFloatingSubmitButton loadingLabel="Gennemfører køb...">Gennemfør
                            betaling</FormikFloatingSubmitButton>
                        : <FormikSubmitButton loadingLabel="Gennemfører køb...">Gennemfør
                            betaling</FormikSubmitButton>
                    }
                </LocalizedStrict>
            </Form>
        </Formik>
    )
}
